/**
 * Exportação de listagem — CSV e o texto que alimenta a view de impressão.
 *
 * Não existe nenhuma biblioteca de PDF no projeto (nem `jspdf`, nem
 * `pdfmake`, nem `react-to-print`), e adicionar uma é dependência nova para
 * decidir, não para assumir de passagem. A saída "PDF" deste sistema é uma
 * VIEW HTML própria, no design system do Lumma (ver `BotaoExportar.tsx`),
 * impressa pelo diálogo nativo do navegador — a pessoa escolhe "Salvar como
 * PDF" no próprio diálogo, que é como a maioria dos ERPs web resolve isto sem
 * dependência nova. A decisão está registrada em `docs/de-para.md`.
 *
 * O CSV é gerado aqui, no FRONTEND, a partir dos dados já carregados na tela
 * — é dado tabular simples, sem estilo, e não precisa de processamento no
 * servidor.
 *
 * As mesmas `colunas` alimentam o CSV e a tabela impressa: escrever a
 * conversão de cada campo duas vezes — uma para o arquivo, outra para a tela
 * — é ter duas fontes de verdade sobre o que "nome do cliente" significa, e
 * elas divergem na primeira mudança que só passar por uma.
 */

/** Uma coluna do relatório: como rotular e como ler o valor de cada linha. */
export type ColunaExportacao<T> = {
  cabecalho: string
  valor: (linha: T) => string
  /** Só a view impressa lê isto — dinheiro e contagem alinham à direita. */
  alinhamento?: 'esquerda' | 'direita'
}

/**
 * Gera o texto do CSV — RFC 4180, com `;` como separador de campo.
 *
 * `;` e não `,`: é o separador que o Excel em PT-BR espera por padrão, porque
 * a vírgula já é o separador decimal daquela localidade — um CSV com `,`
 * abre como uma coluna só.
 *
 * `\r\n` entre linhas é a quebra que a RFC define, e o que o Excel espera.
 */
export function gerarCSV<T>(colunas: ColunaExportacao<T>[], linhas: T[]): string {
  const cabecalho = colunas.map((coluna) => escaparCampoCSV(coluna.cabecalho)).join(';')
  const corpo = linhas.map((linha) =>
    colunas.map((coluna) => escaparCampoCSV(coluna.valor(linha))).join(';'),
  )
  return [cabecalho, ...corpo].join('\r\n')
}

/**
 * Escapa um campo pela RFC 4180: aspas, `;` ou quebra de linha dentro do
 * valor exigem o campo inteiro entre aspas, com toda aspa interna dobrada.
 */
function escaparCampoCSV(valor: string): string {
  if (/["\r\n;]/.test(valor)) {
    return `"${valor.replace(/"/g, '""')}"`
  }
  return valor
}

/** BOM UTF-8 na frente do arquivo — sem ele, o Excel lê acento como lixo. */
const BOM_UTF8 = '﻿'

/**
 * Baixa um texto como arquivo, pelo mecanismo padrão do navegador: um link
 * temporário com `download`, criado, clicado e removido sem nunca aparecer
 * na tela.
 */
export function baixarArquivo(nomeArquivo: string, conteudo: string, tipoMime: string): void {
  const blob = new Blob([conteudo], { type: tipoMime })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Baixa o CSV de uma listagem, com nome "base-2026-09-07.csv". */
export function baixarCSV<T>(nomeBase: string, colunas: ColunaExportacao<T>[], linhas: T[]): void {
  const conteudo = BOM_UTF8 + gerarCSV(colunas, linhas)
  baixarArquivo(nomeDoArquivo(nomeBase, 'csv'), conteudo, 'text/csv;charset=utf-8;')
}

/** "clientes" → "clientes-2026-09-07.csv" — a data de hoje, no fuso local. */
export function nomeDoArquivo(base: string, extensao: string): string {
  return `${base}-${dataDeHojeISO()}.${extensao}`
}

function dataDeHojeISO(): string {
  const agora = new Date()
  const ano = agora.getFullYear()
  const mes = String(agora.getMonth() + 1).padStart(2, '0')
  const dia = String(agora.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}
